import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Truck, Lock, Phone, Loader2, Eye, EyeOff, User, Mail, Car, CheckCircle2, Boxes } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useDriverAuth, API } from '../context/DriverAuthContext';
import { useSettings } from '../context/SettingsContext';
import { useToast } from '../hooks/use-toast';
import PWAInstallPrompt from '../components/PWAInstallPrompt';

const FALLBACK_LOGO = '';

const DriverSignup = () => {
  const { token } = useDriverAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', phone: '', email: '', vehicle: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [categories, setCategories] = useState([]);          // available list from /api/categories
  const [pickedCats, setPickedCats] = useState([]);          // chosen slugs

  useEffect(() => {
    axios
      .get(`${API}/categories`)
      .then(({ data }) => setCategories(data || []))
      .catch(() => setCategories([]));
  }, []);

  const toggleCat = (slug) => {
    setPickedCats((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  };

  if (token) return <Navigate to="/driver" replace />;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast({ title: 'Password too short', description: 'Use at least 6 characters.' });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/driver/signup`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        vehicle: form.vehicle.trim(),
        password: form.password,
        categories: pickedCats,
      });
      setSubmitted(true);
    } catch (err) {
      toast({
        title: 'Sign-up failed',
        description: err?.response?.data?.detail || 'Please try again',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen w-full flex items-center justify-center px-5 py-12 bg-[#0B1220] relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '24px 24px' }}
      />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-emerald-500/20 blur-[120px]" />

      <div className="relative w-full max-w-md">
        {/* Brand */}
        <div className="flex flex-col items-center gap-3 mb-6">
          <img
            src={settings?.logo_image || FALLBACK_LOGO}
            alt="Mohammed Riaz & Partner LLC"
            className="h-16 w-auto rounded-2xl shadow-lg shadow-black/30 object-contain bg-white p-1.5"
            data-testid="driver-signup-logo"
          />
          <div className="text-center">
            <div className="font-display text-xl font-bold text-white leading-none">Mohammed Riaz</div>
            <div className="text-[10px] font-bold tracking-[0.25em] text-emerald-300 uppercase mt-1.5">
              Driver Portal
            </div>
          </div>
        </div>

        <div className="bg-[#111827] border border-white/10 rounded-3xl shadow-2xl p-8 backdrop-blur">
          {submitted ? (
            <div className="text-center py-4" data-testid="driver-signup-success">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 mx-auto flex items-center justify-center mb-4">
                <CheckCircle2 className="w-9 h-9 text-emerald-400" />
              </div>
              <h1 className="font-display text-2xl text-white mb-2">Application received</h1>
              <p className="text-slate-400 text-sm leading-relaxed">
                Thanks for applying! An admin will review your details and activate your account shortly. We'll send you an email the moment you're approved.
              </p>
              <Button
                onClick={() => navigate('/driver/login')}
                className="mt-6 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                data-testid="driver-signup-back-to-login"
              >
                Back to login
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/40">
                  <Truck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-[10px] font-bold tracking-[0.25em] text-emerald-300 uppercase">Driver Portal</div>
                  <h1 className="font-display text-2xl text-white leading-tight">Apply to drive</h1>
                </div>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed mb-5">
                Fill in your details below. Your account will be created in <span className="text-amber-300 font-semibold">pending</span> status until an admin approves it.
              </p>

              <form onSubmit={submit} className="space-y-3" data-testid="driver-signup-form">
                <FieldIcon icon={User} label="Full name *">
                  <Input value={form.name} onChange={update('name')} required className="h-12 pl-10 rounded-xl bg-[#0B1220] border-white/10 text-white" data-testid="driver-signup-name" />
                </FieldIcon>
                <FieldIcon icon={Phone} label="Phone * (used to sign in)">
                  <Input
                    value={form.phone}
                    onChange={update('phone')}
                    required
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    pattern="[0-9+\s]*"
                    placeholder="e.g. 99000111"
                    className="h-12 pl-10 rounded-xl bg-[#0B1220] border-white/10 text-white placeholder:text-slate-600"
                    data-testid="driver-signup-phone"
                  />
                </FieldIcon>
                <FieldIcon icon={Mail} label="Email">
                  <Input type="email" value={form.email} onChange={update('email')} className="h-12 pl-10 rounded-xl bg-[#0B1220] border-white/10 text-white" data-testid="driver-signup-email" />
                </FieldIcon>
                <FieldIcon icon={Car} label="Vehicle">
                  <Input value={form.vehicle} onChange={update('vehicle')} placeholder="e.g. Van OM-A1234" className="h-12 pl-10 rounded-xl bg-[#0B1220] border-white/10 text-white placeholder:text-slate-600" data-testid="driver-signup-vehicle" />
                </FieldIcon>

                <div data-testid="driver-signup-categories">
                  <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                    <Boxes className="w-3.5 h-3.5" /> Delivery divisions
                    <span className="text-slate-500 font-normal normal-case tracking-normal text-xs">(optional — leave empty to deliver any product)</span>
                  </label>
                  {categories.length === 0 ? (
                    <div className="rounded-xl bg-[#0B1220] border border-white/10 px-3 py-3 text-xs text-slate-500">
                      Loading divisions…
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {categories.map((c) => {
                        const checked = pickedCats.includes(c.slug);
                        return (
                          <label
                            key={c.slug}
                            className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 cursor-pointer transition select-none ${
                              checked
                                ? 'bg-emerald-500/10 border-emerald-400 text-emerald-200'
                                : 'bg-[#0B1220] border-white/10 text-slate-300 hover:border-white/30'
                            }`}
                            data-testid={`driver-signup-cat-${c.slug}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleCat(c.slug)}
                              className="w-4 h-4 accent-emerald-500"
                            />
                            <span className="text-sm font-semibold truncate">{c.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  <div className="mt-2 text-[11px] text-slate-500 leading-relaxed">
                    Orders containing only items from your selected divisions will be auto-assigned to you.
                  </div>
                </div>

                <FieldIcon icon={Lock} label="Password * (min 6 characters)">
                  <Input type={showPwd ? 'text' : 'password'} value={form.password} onChange={update('password')} required minLength={6}
                    className="h-12 pl-10 pr-12 rounded-xl bg-[#0B1220] border-white/10 text-white" data-testid="driver-signup-password" />
                  <button type="button" onClick={() => setShowPwd((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-300"
                    aria-label={showPwd ? 'Hide password' : 'Show password'}>
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </FieldIcon>

                <Button type="submit" disabled={loading}
                  className="w-full h-12 mt-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2"
                  data-testid="driver-signup-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Submit application
                </Button>
              </form>

              <div className="text-center text-xs text-slate-400 mt-5">
                Already approved?{' '}
                <Link to="/driver/login" className="text-emerald-300 font-semibold hover:underline">
                  Sign in instead
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
      <PWAInstallPrompt />
    </main>
  );
};

const FieldIcon = ({ icon: Icon, label, children }) => (
  <div>
    <label className="text-[10px] font-bold text-slate-300 uppercase tracking-wider">{label}</label>
    <div className="relative mt-1">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 z-10" />
      {children}
    </div>
  </div>
);

export default DriverSignup;
